import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import MembershipPlanCard from './components/MembershipPlanCard';
import PasswordStrengthIndicator from './components/PasswordStrengthIndicator';
import RegistrationProgress from './components/RegistrationProgress';
import FitnessGoalSelector from './components/FitnessGoalSelector';
import { useAuth } from '../../contexts/AuthContext';

const Register = () => {
  const navigate = useNavigate();
  const { signUp, createTenant, supabase } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    accountType: 'member',
    gymName: '',
    gymAddress: '',
    gymPhone: '',
    gymEmail: '',
    fitnessGoals: [],
    experienceLevel: '',
    selectedPlan: '',
    agreeTerms: false,
    agreePrivacy: false,
    paymentMethod: 'online'
  });

  const [errors, setErrors] = useState({});

  const membershipPlans = [
    {
      id: 'basic',
      name: 'Basic',
      price: 29,
      period: 'month',
      features: [
        { text: 'Access to gym equipment', included: true },
        { text: 'Locker room access', included: true },
        { text: 'Mobile app access', included: true },
        { text: 'Group fitness classes', included: false },
        { text: 'Personal training sessions', included: false },
        { text: 'Nutrition consultation', included: false }
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 59,
      period: 'month',
      features: [
        { text: 'Access to gym equipment', included: true },
        { text: 'Locker room access', included: true },
        { text: 'Mobile app access', included: true },
        { text: 'Unlimited group fitness classes', included: true },
        { text: '2 personal training sessions/month', included: true },
        { text: 'Nutrition consultation', included: false }
      ]
    },
    {
      id: 'elite',
      name: 'Elite',
      price: 99,
      period: 'month',
      features: [
        { text: 'Access to gym equipment', included: true },
        { text: 'Locker room access', included: true },
        { text: 'Mobile app access', included: true },
        { text: 'Unlimited group fitness classes', included: true },
        { text: '4 personal training sessions/month', included: true },
        { text: 'Monthly nutrition consultation', included: true }
      ]
    }
  ];

  const fitnessGoals = [
    {
      id: 'weight-loss',
      label: 'Weight Loss',
      icon: 'TrendingDown',
      description: 'Burn calories and reduce body fat through cardio and strength training'
    },
    {
      id: 'muscle-gain',
      label: 'Muscle Gain',
      icon: 'Dumbbell',
      description: 'Build lean muscle mass with progressive resistance training'
    },
    {
      id: 'endurance',
      label: 'Endurance',
      icon: 'Activity',
      description: 'Improve cardiovascular fitness and stamina for longer workouts'
    },
    {
      id: 'flexibility',
      label: 'Flexibility',
      icon: 'Sparkles',
      description: 'Enhance range of motion and reduce injury risk through stretching'
    },
    {
      id: 'strength',
      label: 'Strength',
      icon: 'Zap',
      description: 'Increase overall power and functional strength for daily activities'
    },
    {
      id: 'wellness',
      label: 'General Wellness',
      icon: 'Heart',
      description: 'Maintain healthy lifestyle with balanced fitness and nutrition'
    }
  ];

  const experienceLevelOptions = [
    { value: 'beginner', label: 'Beginner - New to fitness' },
    { value: 'intermediate', label: 'Intermediate - 6+ months experience' },
    { value: 'advanced', label: 'Advanced - 2+ years experience' },
    { value: 'expert', label: 'Expert - Professional level' }
  ];

  const accountTypeOptions = [
    { value: 'member', label: 'Gym Member' },
    { value: 'gym_owner', label: 'Gym Owner' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleGoalToggle = (goalId) => {
    setFormData(prev => ({
      ...prev,
      fitnessGoals: prev?.fitnessGoals?.includes(goalId)
        ? prev?.fitnessGoals?.filter(id => id !== goalId)
        : [...prev?.fitnessGoals, goalId]
    }));
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData?.fullName?.trim()?.length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/?.test(formData?.password)) {
      newErrors.password = 'Password must include uppercase, lowercase, number, and special character';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/?.test(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData?.accountType) {
      newErrors.accountType = 'Please select an account type';
    }

    // Gym owner specific validation
    if (formData?.accountType === 'gym_owner') {
      if (!formData?.gymName?.trim()) {
        newErrors.gymName = 'Gym name is required for gym owners';
      }
      if (!formData?.gymAddress?.trim()) {
        newErrors.gymAddress = 'Gym address is required';
      }
      if (!formData?.gymPhone?.trim()) {
        newErrors.gymPhone = 'Gym phone is required';
      }
      if (!formData?.gymEmail?.trim()) {
        newErrors.gymEmail = 'Gym email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.gymEmail)) {
        newErrors.gymEmail = 'Please enter a valid gym email';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (formData?.accountType === 'member') {
      if (formData?.fitnessGoals?.length === 0) {
        newErrors.fitnessGoals = 'Please select at least one fitness goal';
      }

      if (!formData?.experienceLevel) {
        newErrors.experienceLevel = 'Please select your experience level';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};

    if (formData?.accountType === 'member' && !formData?.selectedPlan) {
      newErrors.selectedPlan = 'Please select a membership plan';
    }

    if (!formData?.agreeTerms) {
      newErrors.agreeTerms = 'You must agree to the terms and conditions';
    }

    if (!formData?.agreePrivacy) {
      newErrors.agreePrivacy = 'You must agree to the privacy policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    let isValid = false;

    if (currentStep === 1) {
      isValid = validateStep1();
      if (isValid && formData?.accountType === 'gym_owner') {
        // Skip step 2 for gym owners, go directly to step 3
        setCurrentStep(3);
        return;
      }
    } else if (currentStep === 2) {
      isValid = validateStep2();
    }

    if (isValid) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handleBack = () => {
    if (currentStep === 3 && formData?.accountType === 'gym_owner') {
      // For gym owners, go back to step 1 from step 3
      setCurrentStep(1);
    } else {
      setCurrentStep(prev => Math.max(prev - 1, 1));
    }
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      // Step 1: Sign up user
      const { data: authData, error: signUpError } = await signUp(
        formData?.email,
        formData?.password,
        formData?.fullName,
        formData?.phone,
        formData?.accountType === 'gym_owner' ? 'gym_owner' : 'member'
      );

      if (signUpError) {
        console.error('Signup error:', signUpError)
        setErrors({ submit: signUpError?.message || 'Failed to create account. Please try again.' });
        setIsSubmitting(false);
        return;
      }

      if (!authData?.user) {
        setErrors({ submit: 'Account created but user data not available. Please try logging in.' });
        setIsSubmitting(false);
        return;
      }

      // Step 2: Handle gym owner tenant creation
      if (formData?.accountType === 'gym_owner') {
        const gymSlug = formData?.gymName?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
        
        const { error: tenantError } = await createTenant({
          name: formData?.gymName,
          slug: gymSlug,
          address: formData?.gymAddress,
          phone: formData?.gymPhone,
          email: formData?.gymEmail
        });

        if (tenantError) {
          console.error('Tenant creation error:', tenantError)
          setErrors({ submit: tenantError?.message || 'Failed to create gym. Please contact support.' });
          setIsSubmitting(false);
          return;
        }

        // Wait a moment for context to update
        await new Promise(resolve => setTimeout(resolve, 500));
        navigate('/admin-dashboard');
      } else {
        // Step 3: Handle member plan assignment
        if (formData?.selectedPlan && authData?.user?.id) {
          try {
            // Update member's plan in tenant_members
            const { error: planError } = await supabase
              ?.from('tenant_members')
              ?.update({ membership_plan: formData?.selectedPlan })
              ?.eq('user_id', authData?.user?.id)
            
            if (planError) {
              console.error('Plan update error:', planError)
              // Don't fail, plan can be updated later
            }
          } catch (planErr) {
            console.error('Error updating plan:', planErr)
          }
        }

        // Wait a moment for context to update
        await new Promise(resolve => setTimeout(resolve, 500));
        navigate('/member-dashboard');
      }
    } catch (err) {
      console.error('Unexpected error during signup:', err)
      setErrors({ submit: 'An unexpected error occurred. Please try again or contact support.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-[1440px] px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
        <div className="mb-6 md:mb-8">
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="ArrowLeft" size={16} />
            Back to Login
          </Link>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="flex h-14 w-14 md:h-16 md:w-16 items-center justify-center rounded-xl bg-primary/10">
                <Icon name="Dumbbell" size={32} color="var(--color-primary)" strokeWidth={2.5} />
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground">
                Join FitWellness
              </h1>
            </div>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Start your fitness journey today with personalized workout plans and expert guidance
            </p>
          </div>

          <RegistrationProgress currentStep={currentStep} totalSteps={3} />

          <div className="bg-card rounded-xl shadow-lg border border-border p-6 md:p-8 lg:p-10">
            <form onSubmit={handleSubmit}>
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-2">
                      Account Information
                    </h2>
                    <p className="text-sm md:text-base text-muted-foreground">
                      Create your FitWellness account to get started
                    </p>
                  </div>

                  <Input
                    label="Full Name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData?.fullName}
                    onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                    error={errors?.fullName}
                    required
                  />

                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="your.email@example.com"
                    description="We'll send your account verification to this email"
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    error={errors?.email}
                    required
                  />

                  <div>
                    <div className="relative">
                      <Input
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a strong password"
                        value={formData?.password}
                        onChange={(e) => handleInputChange('password', e?.target?.value)}
                        error={errors?.password}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-smooth"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
                      </button>
                    </div>
                    <PasswordStrengthIndicator password={formData?.password} />
                  </div>

                  <div className="relative">
                    <Input
                      label="Confirm Password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-enter your password"
                      value={formData?.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                      error={errors?.confirmPassword}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-smooth"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={20} />
                    </button>
                  </div>

                  <Input
                    label="Phone Number"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={formData?.phone}
                    onChange={(e) => handleInputChange('phone', e?.target?.value)}
                    error={errors?.phone}
                    required
                  />

                  <div>
                    <label htmlFor="accountType" className="block text-sm font-medium text-foreground mb-2">
                      Account Type *
                    </label>
                    <Select
                      id="accountType"
                      value={formData?.accountType}
                      onChange={(value) => handleInputChange('accountType', value)}
                      options={accountTypeOptions}
                      placeholder="Select account type"
                      error={errors?.accountType}
                      disabled={isSubmitting}
                    />
                    <p className="mt-1 text-sm text-muted-foreground">
                      Select the type of account you want to create
                    </p>
                    {errors?.accountType && (
                      <p className="mt-2 text-sm text-destructive flex items-center gap-1">
                        <Icon name="AlertCircle" size={16} />
                        {errors?.accountType}
                      </p>
                    )}
                  </div>

                  {/* Gym Owner Fields */}
                  {formData?.accountType === 'gym_owner' && (
                    <div className="space-y-4 p-4 bg-muted/30 rounded-lg border border-border">
                      <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Icon name="Building" size={18} color="var(--color-primary)" />
                        Gym Information
                      </h3>
                      
                      <div>
                        <label htmlFor="gymName" className="block text-sm font-medium text-foreground mb-2">
                          Gym Name *
                        </label>
                        <Input
                          id="gymName"
                          type="text"
                          value={formData?.gymName}
                          onChange={(e) => handleInputChange('gymName', e?.target?.value)}
                          placeholder="Enter your gym name"
                          error={errors?.gymName}
                          disabled={isSubmitting}
                        />
                      </div>

                      <div>
                        <label htmlFor="gymAddress" className="block text-sm font-medium text-foreground mb-2">
                          Gym Address *
                        </label>
                        <Input
                          id="gymAddress"
                          type="text"
                          value={formData?.gymAddress}
                          onChange={(e) => handleInputChange('gymAddress', e?.target?.value)}
                          placeholder="Enter gym address"
                          error={errors?.gymAddress}
                          disabled={isSubmitting}
                        />
                      </div>

                      <div>
                        <label htmlFor="gymPhone" className="block text-sm font-medium text-foreground mb-2">
                          Gym Phone *
                        </label>
                        <Input
                          id="gymPhone"
                          type="tel"
                          value={formData?.gymPhone}
                          onChange={(e) => handleInputChange('gymPhone', e?.target?.value)}
                          placeholder="+1 (555) 000-0000"
                          error={errors?.gymPhone}
                          disabled={isSubmitting}
                        />
                      </div>

                      <div>
                        <label htmlFor="gymEmail" className="block text-sm font-medium text-foreground mb-2">
                          Gym Email *
                        </label>
                        <Input
                          id="gymEmail"
                          type="email"
                          value={formData?.gymEmail}
                          onChange={(e) => handleInputChange('gymEmail', e?.target?.value)}
                          placeholder="info@yourgym.com"
                          error={errors?.gymEmail}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end pt-4">
                    <Button
                      type="button"
                      onClick={handleNext}
                      iconName="ArrowRight"
                      iconPosition="right"
                      size="lg"
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2: Fitness Goals & Experience (Members Only) */}
              {currentStep === 2 && formData?.accountType === 'member' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      What are your fitness goals?
                    </h3>
                    <FitnessGoalSelector
                      goals={fitnessGoals}
                      selectedGoals={formData?.fitnessGoals}
                      onToggle={handleGoalToggle}
                      disabled={isSubmitting}
                    />
                    {errors?.fitnessGoals && (
                      <p className="mt-2 text-sm text-destructive flex items-center gap-1">
                        <Icon name="AlertCircle" size={16} />
                        {errors?.fitnessGoals}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="experienceLevel" className="block text-sm font-medium text-foreground mb-2">
                      Experience Level *
                    </label>
                    <Select
                      id="experienceLevel"
                      value={formData?.experienceLevel}
                      onChange={(value) => handleInputChange('experienceLevel', value)}
                      options={experienceLevelOptions}
                      placeholder="Select your experience level"
                      error={errors?.experienceLevel}
                      disabled={isSubmitting}
                    />
                    {errors?.experienceLevel && (
                      <p className="mt-2 text-sm text-destructive flex items-center gap-1">
                        <Icon name="AlertCircle" size={16} />
                        {errors?.experienceLevel}
                      </p>
                    )}
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button
                      type="button"
                      onClick={handleBack}
                      variant="outline"
                      iconName="ArrowLeft"
                      iconPosition="left"
                      size="lg"
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={handleNext}
                      iconName="ArrowRight"
                      iconPosition="right"
                      size="lg"
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2: Gym Owner Confirmation */}
              {currentStep === 2 && formData?.accountType === 'gym_owner' && (
                <div className="space-y-6">
                  <div className="text-center py-8">
                    <div className="flex justify-center mb-4">
                      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                        <Icon name="Building" size={40} color="var(--color-primary)" />
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      Almost There!
                    </h3>
                    <p className="text-muted-foreground">
                      Review your gym information and proceed to complete registration.
                    </p>
                  </div>

                  <div className="bg-muted/30 rounded-lg p-6 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Gym Name:</span>
                      <span className="text-sm font-medium text-foreground">{formData?.gymName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Address:</span>
                      <span className="text-sm font-medium text-foreground">{formData?.gymAddress}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Phone:</span>
                      <span className="text-sm font-medium text-foreground">{formData?.gymPhone}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Email:</span>
                      <span className="text-sm font-medium text-foreground">{formData?.gymEmail}</span>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button
                      type="button"
                      onClick={handleBack}
                      variant="outline"
                      iconName="ArrowLeft"
                      iconPosition="left"
                      size="lg"
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={handleNext}
                      iconName="ArrowRight"
                      iconPosition="right"
                      size="lg"
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Membership Plan & Terms */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  {formData?.accountType === 'member' && (
                    <>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-4">
                          Choose Your Membership Plan
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {membershipPlans?.map((plan) => (
                            <MembershipPlanCard
                              key={plan?.id}
                              plan={plan}
                              isSelected={formData?.selectedPlan === plan?.id}
                              onSelect={() => handleInputChange('selectedPlan', plan?.id)}
                              isPopular={plan?.id === 'premium'}
                              disabled={isSubmitting}
                            />
                          ))}
                        </div>
                        {errors?.selectedPlan && (
                          <p className="mt-2 text-sm text-destructive flex items-center gap-1">
                            <Icon name="AlertCircle" size={16} />
                            {errors?.selectedPlan}
                          </p>
                        )}
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-4">
                          Choose Payment Method
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div
                            onClick={() => handleInputChange('paymentMethod', 'online')}
                            className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
                              formData?.paymentMethod === 'online' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                            }`}
                          >
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Icon name="CreditCard" size={24} className="text-primary" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-foreground mb-1">Online Payment</h4>
                                <p className="text-sm text-muted-foreground mb-2">
                                  Pay securely with credit card or PayPal
                                </p>
                                <div className="flex items-center gap-2">
                                  <Icon name="Shield" size={14} className="text-success" />
                                  <span className="text-xs text-success">Secure & Instant</span>
                                </div>
                              </div>
                              {formData?.paymentMethod === 'online' && (
                                <Icon name="CheckCircle2" size={24} className="text-primary" />
                              )}
                            </div>
                          </div>

                          <div
                            onClick={() => handleInputChange('paymentMethod', 'gym')}
                            className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
                              formData?.paymentMethod === 'gym' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                            }`}
                          >
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Icon name="Building2" size={24} className="text-primary" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-foreground mb-1">Pay at Gym</h4>
                                <p className="text-sm text-muted-foreground mb-2">
                                  Complete payment in person at the gym
                                </p>
                                <div className="flex items-center gap-2">
                                  <Icon name="MapPin" size={14} className="text-primary" />
                                  <span className="text-xs text-primary">Visit Us</span>
                                </div>
                              </div>
                              {formData?.paymentMethod === 'gym' && (
                                <Icon name="CheckCircle2" size={24} className="text-primary" />
                              )}
                            </div>
                          </div>
                        </div>
                        {errors?.paymentMethod && (
                          <p className="mt-2 text-sm text-destructive flex items-center gap-1">
                            <Icon name="AlertCircle" size={16} />
                            {errors?.paymentMethod}
                          </p>
                        )}

                        {formData?.paymentMethod === 'gym' && (
                          <div className="mt-4 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                            <div className="flex items-start gap-3">
                              <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                              <div>
                                <h4 className="font-semibold text-foreground mb-1">Payment Instructions</h4>
                                <p className="text-sm text-muted-foreground">
                                  Your account will be created, but your membership will be activated once you complete payment at our gym location. Please bring a valid ID and visit us during business hours.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <div className="space-y-4 p-6 bg-muted/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="agreeTerms"
                        checked={formData?.agreeTerms}
                        onCheckedChange={(checked) => handleInputChange('agreeTerms', checked)}
                        disabled={isSubmitting}
                      />
                      <label htmlFor="agreeTerms" className="text-sm text-foreground cursor-pointer">
                        I agree to the{' '}
                        <Link to="/terms" className="text-primary hover:underline">
                          Terms and Conditions
                        </Link>
                      </label>
                    </div>
                    {errors?.agreeTerms && (
                      <p className="text-sm text-destructive flex items-center gap-1 ml-8">
                        <Icon name="AlertCircle" size={16} />
                        {errors?.agreeTerms}
                      </p>
                    )}

                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="agreePrivacy"
                        checked={formData?.agreePrivacy}
                        onCheckedChange={(checked) => handleInputChange('agreePrivacy', checked)}
                        disabled={isSubmitting}
                      />
                      <label htmlFor="agreePrivacy" className="text-sm text-foreground cursor-pointer">
                        I agree to the{' '}
                        <Link to="/privacy" className="text-primary hover:underline">
                          Privacy Policy
                        </Link>
                      </label>
                    </div>
                    {errors?.agreePrivacy && (
                      <p className="text-sm text-destructive flex items-center gap-1 ml-8">
                        <Icon name="AlertCircle" size={16} />
                        {errors?.agreePrivacy}
                      </p>
                    )}
                  </div>

                  {errors?.submit && (
                    <div className="p-4 bg-destructive/10 border border-destructive rounded-lg">
                      <p className="text-sm text-destructive flex items-center gap-2">
                        <Icon name="AlertCircle" size={16} />
                        {errors?.submit}
                      </p>
                    </div>
                  )}

                  <div className="flex justify-between pt-4">
                    <Button
                      type="button"
                      onClick={handleBack}
                      variant="outline"
                      iconName="ArrowLeft"
                      iconPosition="left"
                      size="lg"
                      disabled={isSubmitting}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSubmit}
                      iconName="Check"
                      iconPosition="right"
                      size="lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Creating Account...' : 'Complete Registration'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </div>

          <div className="text-center mt-6 md:mt-8">
            <p className="text-sm md:text-base text-muted-foreground">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="font-medium text-accent hover:text-accent/90 transition-smooth"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;